import { TableCellTypes } from '@carboledger/ui';
import {
  makeLinkCell,
  makeUnitTypeCell,
  makeUnitScopedCell,
  makeDiscrepanciesCell,
} from '@/components/common';
import { CONTRACT_COLUMNS, CONTRACT_SORT_FIELDS } from './contracts.constants';
import { buildContractStatusVariants } from '@/config/statuses';
import { CONTRACT_STATUS_TONE } from '@/config/statusTones';
import { buildRowUnitOptions, unitTypeLabel } from './contractUnits';

// One entry per column; `sortable` is derived from CONTRACT_SORT_FIELDS below.
const canSort = (id) => Boolean(CONTRACT_SORT_FIELDS[id]);

const renderUnitTypeCell = makeUnitTypeCell((t, row) => buildRowUnitOptions(t, row.byUnit));
const renderContractedQuantity = makeUnitScopedCell('contractedQuantity');
const renderShippedQuantity = makeUnitScopedCell('shippedQuantity');
const renderOutstandingQuantity = makeUnitScopedCell('outstandingQuantity');

export const buildContractsColumns = (t, { unitHeaderSelect, onOpenDiscrepancy, onOpenContract }) =>
  [
    {
      id: CONTRACT_COLUMNS.contractId,
      header: t('FIELD_CONTRACT_ID'),
      type: TableCellTypes.custom,
      width: 140,
      pinnable: true,
      cell: makeLinkCell(onOpenContract),
    },
    {
      id: CONTRACT_COLUMNS.materialName,
      header: t('FIELD_MATERIAL_NAME'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: CONTRACT_COLUMNS.totalShipmentLinked,
      header: t('FIELD_TOTAL_SHIPMENT_LINKED'),
      type: TableCellTypes.text,
      width: 160,
    },
    {
      id: CONTRACT_COLUMNS.supplierName,
      header: t('FIELD_SUPPLIER_NAME'),
      type: TableCellTypes.text,
      width: 150,
    },
    {
      id: CONTRACT_COLUMNS.status,
      header: t('FIELD_STATUS'),
      type: TableCellTypes.statusDot,
      width: 110,
      variantMap: buildContractStatusVariants(t, CONTRACT_STATUS_TONE),
    },
    {
      id: CONTRACT_COLUMNS.facilityName,
      header: t('FIELD_FACILITY_NAME'),
      type: TableCellTypes.text,
      width: 143,
    },
    {
      id: CONTRACT_COLUMNS.terminal,
      header: t('FIELD_TERMINAL'),
      type: TableCellTypes.text,
      width: 141,
    },
    {
      id: CONTRACT_COLUMNS.unitType,
      header: t('FIELD_UNIT_TYPE', { unit: unitTypeLabel(t, unitHeaderSelect.value) }),
      type: TableCellTypes.custom,
      width: 252,
      cell: renderUnitTypeCell,
      headerSelect: unitHeaderSelect,
    },
    {
      id: CONTRACT_COLUMNS.contractedQuantity,
      header: t('FIELD_CONTRACTED_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderContractedQuantity,
    },
    {
      id: CONTRACT_COLUMNS.shippedQuantity,
      header: t('FIELD_SHIPPED_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderShippedQuantity,
    },
    {
      id: CONTRACT_COLUMNS.outstandingQuantity,
      header: t('FIELD_OUTSTANDING_QUANTITY'),
      type: TableCellTypes.custom,
      width: 200,
      cell: renderOutstandingQuantity,
    },
    {
      id: CONTRACT_COLUMNS.ghgEmissions,
      header: t('FIELD_GHG_EMISSIONS'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: CONTRACT_COLUMNS.ghgEmissionSavings,
      header: t('FIELD_GHG_EMISSION_SAVINGS'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: CONTRACT_COLUMNS.rawMaterialType,
      header: t('FIELD_RAW_MATERIAL_TYPE'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: CONTRACT_COLUMNS.countryOfOrigin,
      header: t('FIELD_COUNTRY_OF_ORIGIN'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: CONTRACT_COLUMNS.feedstockCategory,
      header: t('FIELD_FEEDSTOCK_CATEGORY'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: CONTRACT_COLUMNS.allocationPreference,
      header: t('FIELD_ALLOCATION_PREFERENCE'),
      type: TableCellTypes.text,
      width: 200,
    },
    {
      id: CONTRACT_COLUMNS.deliverBy,
      header: t('FIELD_DELIVER_BY'),
      type: TableCellTypes.date,
      width: 150,
    },
    {
      id: CONTRACT_COLUMNS.discrepancies,
      header: t('FIELD_DISCREPANCIES'),
      type: TableCellTypes.custom,
      width: 200,
      cell: makeDiscrepanciesCell(onOpenDiscrepancy),
    },
  ].map((column) => ({ ...column, sortable: canSort(column.id) }));
